import { SpotStatus, type RoomLayoutSpot } from '../components/RoomLayoutMap/RoomLayoutMap.types';
import { getCanonicalSpotName, normalizeSpot } from '../components/RoomLayoutMap/spotKey';
import {
  MissingSpotFailureReason,
  type BookSpotReserveMeta,
  type BookSpotReserveParams,
  type InteractiveSpotBookingParamsLike,
  type LastBookSpotContext,
  type LastChangeSpotContext,
  type SpotBookingCtaMeta,
  type SpotTapMeta
} from './types';
import {
  buildBookSpotReserveMeta,
  isFirstSpotAssignment,
  resolvePreviousSpotIdForChangeSpot
} from './spotBookingLogic';
import { buildBookParamsForReserve, getSpotPayloadForBookSpot } from './bookSpotReserveParams';

export type ExecuteBookSpotActions = {
  setQuickBookPendingClose: () => void;
  setLastBookSpotContext: (ctx: LastBookSpotContext) => void;
  trackSpotBookingCtaClicked: (meta: SpotBookingCtaMeta) => void;
  trackSpotBookingRequestSent: (meta: LastBookSpotContext) => void;
  trackInteractiveSpotChangeRequestSent: (meta: LastChangeSpotContext) => void;
  setLastChangeSpotContext: (ctx: LastChangeSpotContext) => void;
  onMissingSpotId?: (reason: MissingSpotFailureReason) => void;
  bookSpot: (
    body: { bookingId: string; spotPayload: string },
    meta: BookSpotReserveMeta<BookSpotReserveParams>
  ) => void;
  bookingId: string;
  eventId: string;
  isChangeSpotFlow: boolean;
  previousSpotId: string;
};

export function executeBookSpot<TParams extends InteractiveSpotBookingParamsLike>(
  selectedSpot: RoomLayoutSpot | null,
  isQuickBook: boolean,
  shouldDeferModalClose: boolean,
  params: TParams,
  actions: ExecuteBookSpotActions
): void {
  if (!selectedSpot) return;
  actions.trackSpotBookingCtaClicked({ timeSlotId: actions.eventId, selectedSpotCount: 1 });
  const resolvedPreviousSpotId = actions.isChangeSpotFlow
    ? resolvePreviousSpotIdForChangeSpot(params, actions.previousSpotId)
    : '';
  const normalized = normalizeSpot(selectedSpot);
  const resolvedSpotId = getSpotPayloadForBookSpot(normalized);
  if (resolvedSpotId === null) {
    actions.onMissingSpotId?.(MissingSpotFailureReason.MISSING_SPOT_ID);
    return;
  }
  if (actions.isChangeSpotFlow && resolvedPreviousSpotId === '' && !isFirstSpotAssignment(params)) {
    actions.onMissingSpotId?.(MissingSpotFailureReason.MISSING_PREVIOUS_SPOT_ID);
    return;
  }
  const context: LastBookSpotContext = {
    timeSlotId: actions.eventId,
    spotTypePrefix: selectedSpot.spot_type_prefix ?? '',
    spotNumber: resolvedSpotId
  };
  actions.setLastBookSpotContext(context);
  if (actions.isChangeSpotFlow && resolvedPreviousSpotId !== '') {
    const changeContext: LastChangeSpotContext = {
      timeSlotId: actions.eventId,
      previousSpotId: resolvedPreviousSpotId,
      spotId: resolvedSpotId
    };
    actions.setLastChangeSpotContext(changeContext);
    actions.trackInteractiveSpotChangeRequestSent(changeContext);
  } else {
    actions.trackSpotBookingRequestSent(context);
  }
  if (shouldDeferModalClose) actions.setQuickBookPendingClose();
  const bookParams = buildBookParamsForReserve(
    params,
    actions.isChangeSpotFlow,
    resolvedSpotId,
    normalized.displayLabel
  );
  actions.bookSpot(
    { bookingId: actions.bookingId, spotPayload: resolvedSpotId },
    buildBookSpotReserveMeta(
      isQuickBook,
      params,
      bookParams,
      resolvedPreviousSpotId,
      actions.isChangeSpotFlow
    )
  );
}

export type PerformInteractiveBookSpotArgs<TParams extends InteractiveSpotBookingParamsLike> = {
  bookingId: string;
  selectedSpot: RoomLayoutSpot | null;
  isQuickBook: boolean;
  shouldDeferModalClose: boolean;
  params: TParams;
  dismissBookSpotError: () => void;
  lastAttemptedSpotGridKeyRef: { current: string | null };
  setQuickBookPendingClose: () => void;
  setLastBookSpotContext: (ctx: LastBookSpotContext) => void;
  trackSpotBookingCtaClicked: (meta: SpotBookingCtaMeta) => void;
  trackSpotBookingRequestSent: (meta: LastBookSpotContext) => void;
  trackInteractiveSpotChangeRequestSent: (meta: LastChangeSpotContext) => void;
  setLastChangeSpotContext: (ctx: LastChangeSpotContext) => void;
  onMissingSpotId: (reason: MissingSpotFailureReason) => void;
  bookSpot: ExecuteBookSpotActions['bookSpot'];
  eventId: string;
  isChangeSpotFlow: boolean;
  previousSpotId: string;
};

export function performInteractiveBookSpot<TParams extends InteractiveSpotBookingParamsLike>(
  args: PerformInteractiveBookSpotArgs<TParams>,
  skipInFlightRef: { current: boolean }
): void {
  if (skipInFlightRef.current) return;

  const {
    bookingId,
    selectedSpot,
    isQuickBook,
    shouldDeferModalClose,
    params,
    dismissBookSpotError,
    lastAttemptedSpotGridKeyRef,
    setQuickBookPendingClose,
    setLastBookSpotContext,
    trackSpotBookingCtaClicked,
    trackSpotBookingRequestSent,
    trackInteractiveSpotChangeRequestSent,
    onMissingSpotId,
    bookSpot,
    eventId,
    isChangeSpotFlow,
    previousSpotId
  } = args;

  if (!bookingId) return;
  if (!selectedSpot) return;

  dismissBookSpotError();
  const normalizedSelectedSpot = normalizeSpot(selectedSpot);
  lastAttemptedSpotGridKeyRef.current = getCanonicalSpotName(normalizedSelectedSpot);
  executeBookSpot(selectedSpot, isQuickBook, shouldDeferModalClose, params, {
    setQuickBookPendingClose,
    setLastBookSpotContext,
    trackSpotBookingCtaClicked,
    trackSpotBookingRequestSent,
    trackInteractiveSpotChangeRequestSent,
    setLastChangeSpotContext: args.setLastChangeSpotContext,
    onMissingSpotId,
    bookSpot,
    bookingId,
    eventId,
    isChangeSpotFlow,
    previousSpotId
  });
}

export function buildSpotTapMeta(spot: RoomLayoutSpot, eventId: string): SpotTapMeta {
  const normalized = normalizeSpot(spot);
  return {
    timeSlotId: eventId,
    spotTypePrefix: spot.spot_type_prefix ?? '',
    spotNumber: getSpotPayloadForBookSpot(normalized) ?? '',
    isBookable: spot.status === SpotStatus.BOOKABLE
  };
}

export function handleInteractiveSpotSelect(
  spot: RoomLayoutSpot,
  eventId: string,
  trackSpotTapped: (meta: SpotTapMeta) => void,
  onSelectSpot: (spot: RoomLayoutSpot) => void,
  skipInFlightRef: { current: boolean }
): void {
  if (skipInFlightRef.current) return;

  trackSpotTapped(buildSpotTapMeta(spot, eventId));
  onSelectSpot(spot);
}
